<script setup lang="ts">
import { computed, watch } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { monthlySummariesApi, type CareerOption } from '@/api/monthly-summaries';
import { careers as careerCatalog } from '@/utils/careers';

interface Props {
  modelValue: string[];
  offset: number;
  label?: string;
  variant?: 'underlined' | 'outlined' | 'plain' | 'solo' | 'filled';
  density?: 'default' | 'comfortable' | 'compact';
  maxVisibleChips?: number;
  hideDetails?: boolean | 'auto';
}

interface Emits {
  (e: 'update:modelValue', value: string[]): void;
}

const props = withDefaults(defineProps<Props>(), {
  label: 'Especialidades',
  variant: 'underlined',
  density: undefined,
  maxVisibleChips: 1,
  hideDetails: false,
});

const emit = defineEmits<Emits>();

const FIVE_MINUTES_IN_MS = 300_000;

// Options are month-scoped: only careers with shifts in the selected month
// (already permission-filtered server-side). placeholderData keeps the previous
// month's options visible while the new month loads (no unmount flicker).
const { data: careers } = useQuery({
  queryKey: computed(() => ['dashboardCareers', props.offset]),
  queryFn: () => monthlySummariesApi.careers(`offset=${props.offset}`),
  staleTime: FIVE_MINUTES_IN_MS,
  placeholderData: (previous: CareerOption[] | undefined) => previous,
});

// Drop persisted selections outside the current option list (month navigation,
// revoked career permission, cross-company localStorage leakage). Without this
// the chips render blank and the API silently filters by an invisible career.
watch(careers, (loaded) => {
  if (!loaded) return;
  const valid = new Set(loaded.map(option => option.career));
  const cleaned = props.modelValue.filter(career => valid.has(career));
  if (cleaned.length !== props.modelValue.length) {
    emit('update:modelValue', cleaned);
  }
}, { immediate: true });

function fallbackTitle(career: string): string {
  if (career === 'without_career') return 'Sin especialidad';

  return careerCatalog.find(option => option.value === career)?.title ?? career;
}

const items = computed(() => {
  const loaded = (careers.value ?? []).map(option => ({ value: option.career, title: option.label }));
  const known = new Set(loaded.map(item => item.value));
  // Selections missing from the option list (fetch not yet resolved or failed)
  // still render as labeled chips instead of leaving an invisible active filter;
  // a successful load prunes them right after (see the watch above).
  const missing = props.modelValue
    .filter(career => !known.has(career))
    .map(career => ({ value: career, title: fallbackTitle(career) }));

  return [...loaded, ...missing];
});

const shouldShow = computed(() => items.value.length > 0);

const internalValue = computed({
  get: () => props.modelValue,
  set: (value: string[]) => emit('update:modelValue', value),
});

const selectedCount = computed(() => props.modelValue?.length ?? 0);

function removeItem(value: string) {
  emit('update:modelValue', props.modelValue.filter(career => career !== value));
}
</script>

<template>
  <v-autocomplete
    v-if="shouldShow"
    v-model="internalValue"
    :items="items"
    :label="label"
    :density="density"
    :variant="variant"
    :hide-details="hideDetails"
    multiple
    clearable
    single-line
  >
    <template #selection="{ item, index }">
      <v-chip
        v-if="index < maxVisibleChips"
        closable
        size="small"
        @click:close.stop="removeItem(item.value as string)"
      >
        {{ item.title }}
      </v-chip>
      <span
        v-else-if="index === maxVisibleChips"
        class="text-caption text-medium-emphasis ml-1"
      >
        +{{ selectedCount - maxVisibleChips }}
      </span>
    </template>
  </v-autocomplete>
</template>
